const inferRegionFromLocation = (location = "") => {
  const normalized = String(location).toLowerCase();
  if (normalized.includes("tirana") || normalized.includes("elbasan")) return "central";
  if (normalized.includes("shkod") || normalized.includes("kuks")) return "north";
  if (normalized.includes("fier") || normalized.includes("vlor")) return "south";
  if (normalized.includes("durr")) return "coastal";
  return "central";
};

const inferProductTypes = (description = "") => {
  const normalized = String(description).toLowerCase();
  const values = [];
  if (normalized.includes("fruit")) values.push("fruit");
  if (normalized.includes("vegetable")) values.push("vegetables");
  if (normalized.includes("herb")) values.push("herbs");
  if (normalized.includes("dairy")) values.push("dairy");
  if (normalized.includes("egg")) values.push("eggs");
  if (normalized.includes("honey")) values.push("honey");
  if (values.length === 0) values.push("vegetables");
  return values;
};

export const mapApiFarmToCatalog = (farm, index) => {
  const description = farm.description || "Produkte të freskëta lokale nga prodhues të besuar.";
  const isOrganic = /organic/i.test(description);
  return {
    id: farm.id,
    name: farm.name,
    description,
    location: farm.location || "Shqipëri",
    image: farm.bannerImageUrl || farm.logoImageUrl || null,
    region: inferRegionFromLocation(farm.location),
    productTypes: inferProductTypes(description),
    isOrganic,
    deliveryWindow: ["morning", "afternoon", "evening"][index % 3],
    tags: isOrganic ? ["Organike", "Lokale"] : ["Lokale", "Stinore"],
    primaryAction: "viewFarm",
    secondaryAction: "contact",
    ownerName: farm.ownerName,
    ownerFirstName: farm.ownerFirstName || farm.ownerName?.split(" ")[0],
    ownerLastName: farm.ownerLastName,
    ownerProfilePictureUrl: farm.ownerProfilePictureUrl,
    ownerUserId: farm.ownerUserId,
    // This mapper builds an explicit shape, so anything not listed here never reaches the cards.
    averageRating: Number(farm.averageRating) || 0,
    reviewCount: Number(farm.reviewCount) || 0,
  };
};

export const createDefaultFilters = () => ({
  region: "all",
  productTypes: [],
  organicOnly: false,
  deliveryWindow: "any",
});

export const cloneFilters = (source) => ({
  region: source.region,
  productTypes: [...source.productTypes],
  organicOnly: source.organicOnly,
  deliveryWindow: source.deliveryWindow,
});

export const deliveryRanking = {
  morning: 0,
  afternoon: 1,
  evening: 2,
};

/** Compute filtered + sorted farm list given the current catalog, search query, active filters, and sort option. */
export const computeFilteredFarms = (farmsCatalog, filters, debouncedSearch, sortOption) => {
  const normalizedQuery = debouncedSearch.trim().toLowerCase();

  const matchesSearch = (farm) =>
    !normalizedQuery ||
    farm.name.toLowerCase().includes(normalizedQuery) ||
    farm.location.toLowerCase().includes(normalizedQuery) ||
    farm.tags.some((tag) => tag.toLowerCase().includes(normalizedQuery));

  const matchesFilters = (farm) => {
    const regionOk = filters.region === "all" || farm.region === filters.region;
    const productsOk =
      filters.productTypes.length === 0 ||
      filters.productTypes.some((type) => farm.productTypes.includes(type));
    const organicOk = !filters.organicOnly || farm.isOrganic;
    const deliveryOk =
      filters.deliveryWindow === "any" ||
      farm.deliveryWindow === filters.deliveryWindow;
    return regionOk && productsOk && organicOk && deliveryOk;
  };

  const base = farmsCatalog.filter((farm) => matchesSearch(farm) && matchesFilters(farm));

  if (sortOption === "az") return [...base].sort((a, b) => a.name.localeCompare(b.name));

  if (sortOption === "delivery") {
    return [...base].sort((a, b) => {
      const aRank = deliveryRanking[a.deliveryWindow] ?? Number.MAX_SAFE_INTEGER;
      const bRank = deliveryRanking[b.deliveryWindow] ?? Number.MAX_SAFE_INTEGER;
      if (aRank === bRank) return a.name.localeCompare(b.name);
      return aRank - bRank;
    });
  }

  return base;
};

/** Static option definitions — value + i18n key + fallback label. Used by Farms.js to build
 *  translated option arrays with useMemo without repeating t() boilerplate inline. */
export const REGION_OPTION_DEFS = [
  { value: "all", key: "farms.filters.region.all", defaultValue: "All regions" },
  { value: "central", key: "farms.filters.region.central", defaultValue: "Central" },
  { value: "north", key: "farms.filters.region.north", defaultValue: "North" },
  { value: "south", key: "farms.filters.region.south", defaultValue: "South" },
  { value: "coastal", key: "farms.filters.region.coastal", defaultValue: "Coastal" },
  { value: "highlands", key: "farms.filters.region.highlands", defaultValue: "Highlands" },
];

export const PRODUCT_TYPE_OPTION_DEFS = [
  { value: "vegetables", key: "farms.filters.product.vegetables", defaultValue: "Vegetables" },
  { value: "fruit", key: "farms.filters.product.fruit", defaultValue: "Fruit" },
  { value: "herbs", key: "farms.filters.product.herbs", defaultValue: "Herbs" },
  { value: "dairy", key: "farms.filters.product.dairy", defaultValue: "Dairy" },
  { value: "eggs", key: "farms.filters.product.eggs", defaultValue: "Eggs" },
  { value: "honey", key: "farms.filters.product.honey", defaultValue: "Honey" },
  { value: "flowers", key: "farms.filters.product.flowers", defaultValue: "Edible flowers" },
];

export const DELIVERY_WINDOW_OPTION_DEFS = [
  { value: "any", key: "farms.filters.delivery.any", defaultValue: "Any time" },
  { value: "morning", key: "farms.filters.delivery.morning", defaultValue: "Morning delivery" },
  { value: "afternoon", key: "farms.filters.delivery.afternoon", defaultValue: "Afternoon delivery" },
  { value: "evening", key: "farms.filters.delivery.evening", defaultValue: "Evening delivery" },
];

export const SORT_OPTION_DEFS = [
  { value: "recommended", key: "farms.sort.recommended", defaultValue: "Recommended" },
  { value: "az", key: "farms.sort.az", defaultValue: "Name A\u2013Z" },
  { value: "delivery", key: "farms.sort.delivery", defaultValue: "Delivery window" },
];
